import type { SupabaseClient } from '@supabase/supabase-js';
import type { Product, RoutineSet, IngredientDetail, ProductReview, FaqItem, ClinicalResult, ProductDesignV2 } from './types';

// The Rx.K Seoul catalog (routine products, the 3-Step Set and its page
// product), built from public views only, so any site on the shared
// database can load it with its own Supabase client. Shared between rxk
// and Pick.K Seoul (src/shared/rxk-product is the same folder in both).

// Routine steps get "Step 01/02/03"; any other product (OTHER) lists after them.
const STEP_ORDER: Record<string, number> = { OPEN: 1, TREAT: 2, LOCK: 3 };
export const SET_SLUG = '3-step-set';

export interface CoreProductRow {
  sku: string;
  name_en: string;
  step_label: string;
  volume_ml: string | null;
  msrp_bdt: string;
  status: string;
  gift_box_free?: boolean;
}

export interface ProductContentRow {
  sku: string;
  slug: string;
  subtitle: string | null;
  image: string | null;
  gallery_images: string[];
  image_alt: string | null;
  skin_types: string[];
  how_to_use: string[];
  key_ingredients: { name: string; benefit: string }[];
  ingredient_details: IngredientDetail[];
  inci: string | null;
  reviews: ProductReview[];
  faqs: FaqItem[];
  skin_guide: { type: string; tip: string }[];
  claims: string[];
  description: string | null;
  clinical_results: ClinicalResult[] | null;
  video_url: string | null;
  design_v2?: ProductDesignV2 | null;
}

export function formatPrice(bdt: string | number): string {
  return `৳${Number(bdt).toLocaleString()}`;
}

// One storefront product from its Core row and its rxk_product_content row.
export function toProduct(core: CoreProductRow, content: ProductContentRow): Product {
  const step = STEP_ORDER[core.step_label];
  return {
    id: content.slug,
    sku: core.sku,
    step: step ?? 99,
    stepLabel: step ? `Step 0${step}` : '',
    stepKeyword: core.step_label,
    giftBoxFree: !!core.gift_box_free,
    name: core.name_en,
    subtitle: content.subtitle ?? '',
    volume: core.volume_ml ? `${core.volume_ml}ml` : '',
    price: formatPrice(core.msrp_bdt),
    image: content.image ?? '',
    galleryImages: content.gallery_images ?? [],
    imageAlt: content.image_alt ?? core.name_en,
    skinTypes: content.skin_types ?? [],
    howToUse: content.how_to_use ?? [],
    keyIngredients: content.key_ingredients ?? [],
    ingredientDetails: content.ingredient_details ?? [],
    inci: content.inci ?? '',
    reviews: content.reviews ?? [],
    faqs: content.faqs ?? [],
    skinGuide: content.skin_guide ?? [],
    claims: content.claims ?? [],
    description: content.description ?? '',
    clinicalResults: content.clinical_results ?? undefined,
    videoUrl: content.video_url ?? undefined,
    designV2: content.design_v2 ?? {},
  };
}

export interface Catalog {
  products: Product[];
  routineSet: RoutineSet | null;
  setProduct: Product | null;
  contentRows: ProductContentRow[];
}

// Site-relative media paths (/v2/..., /images/...) point at files hosted
// by rxk; another site passes rxk's origin so they still load there.
const MEDIA = /\.(webp|png|jpe?g|gif|svg|avif|mp4|webm|mov)(\?.*)?$/i;
function withOrigin<T>(value: T, origin: string): T {
  if (!origin) return value;
  if (typeof value === 'string') return (value.startsWith('/') && !value.startsWith('//') && MEDIA.test(value) ? origin + value : value) as T;
  if (Array.isArray(value)) return value.map((v) => withOrigin(v, origin)) as T;
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, withOrigin(v, origin)])) as T;
  return value;
}

// setImage: the set banner photo used when Admin has not set one.
// assetOrigin: rxk's origin, for sites other than rxk (e.g. 'https://rxk.thekseoul.com').
export async function fetchCatalog(client: SupabaseClient, setImage = '', assetOrigin = ''): Promise<Catalog> {
  const [coreRes, contentRes, bundleRes] = await Promise.all([
    client.from('rxk_public_products_v').select('*'),
    client.from('rxk_product_content').select('*'),
    client.from('core_product_bundles_public_v').select('bundle_sku, component_sku, qty_per_bundle, component_msrp_bdt'),
  ]);

  const coreRows = (coreRes.data as CoreProductRow[]) ?? [];
  const contentRows = withOrigin((contentRes.data as ProductContentRow[]) ?? [], assetOrigin);
  setImage = withOrigin(setImage, assetOrigin);
  const contentBySku = new Map<string, ProductContentRow>();
  for (const row of contentRows) contentBySku.set(row.sku, row);

  // The routine set is the bundle made only of routine-step products (the
  // 3-Step Set); its "original price" is what those components cost alone.
  const bundleParts = new Map<string, { sku: string; qty: number; msrp: number }[]>();
  for (const b of (bundleRes.data as { bundle_sku: string; component_sku: string; qty_per_bundle: number; component_msrp_bdt: number | null }[]) ?? []) {
    const list = bundleParts.get(b.bundle_sku) ?? [];
    list.push({ sku: b.component_sku, qty: Number(b.qty_per_bundle) || 1, msrp: Number(b.component_msrp_bdt) || 0 });
    bundleParts.set(b.bundle_sku, list);
  }
  const stepOf = new Map(coreRows.map((c) => [c.sku, c.step_label]));
  const bundles = coreRows.filter((c) => bundleParts.has(c.sku));
  const bundleCore = bundles.find((c) => bundleParts.get(c.sku)!.every((part) => STEP_ORDER[stepOf.get(part.sku) ?? ''])) ?? bundles[0];

  const products: Product[] = [];
  for (const core of coreRows) {
    if (core.sku === bundleCore?.sku) continue;
    const content = contentBySku.get(core.sku);
    if (!content) continue;
    products.push(toProduct(core, content));
  }
  products.sort((a, b) => a.step - b.step);

  let routineSet: RoutineSet | null = null;
  if (bundleCore) {
    const bundleContent = contentBySku.get(bundleCore.sku);
    const originalTotal = (bundleParts.get(bundleCore.sku) ?? []).reduce((sum, part) => sum + part.msrp * part.qty, 0);
    const bundlePrice = Number(bundleCore.msrp_bdt);
    routineSet = {
      sku: bundleCore.sku,
      name: bundleCore.name_en,
      designV2: bundleContent?.design_v2 ?? {},
      price: formatPrice(bundlePrice),
      originalPrice: formatPrice(originalTotal),
      savings: formatPrice(Math.max(originalTotal - bundlePrice, 0)),
      description: bundleContent?.description ?? '',
      // Admin-set image (rxk_product_content.image) wins if present, else
      // the square all-3-products lineup shot — its wide composition crops
      // gracefully into the set banner's landscape photo band.
      image: bundleContent?.image || setImage,
    };
  }

  // The set as a full product too, for the new-design 3-Step page.
  const setContent = bundleCore ? contentBySku.get(bundleCore.sku) : undefined;
  const setProduct = bundleCore && setContent ? { ...toProduct(bundleCore, setContent), id: SET_SLUG } : null;

  return { products, routineSet, setProduct, contentRows };
}
